/* eslint-disable react/no-unstable-nested-components */
import { PopoverTrigger } from "@radix-ui/react-popover";
import { createColumnHelper } from "@tanstack/react-table";
import ReportDateContextPopover from "components/financials/modern/ReportDateContextPopover";
import { STATEMENT_TYPE_ATOM } from "components/financials/modern/atoms";
import IndicatorDocumentationPopover from "components/financials/modern/indicators/IndicatorDocumentationPopover";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import type { SheetIndicator, VerticalFinancialsSheetRow } from "components/financials/modern/types";
import { Popover, PopoverContent } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import getYear from "date-fns/getYear";
import lightFormat from "date-fns/lightFormat";
import { useAtomValue } from "jotai";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import tinycolor from "tinycolor2";
import { IFinancialsStatementType } from "types/graphqlTypes";

type UseTableColumnsParams = {
  loading: boolean;
  rows: VerticalFinancialsSheetRow[];
  indicators: SheetIndicator[];
};
export function useTableColumns({ loading, rows, indicators }: UseTableColumnsParams) {
  const intl = useIntl();
  const theme = useTheme();
  const statementType = useAtomValue(STATEMENT_TYPE_ATOM);
  const columnHelper = createColumnHelper<VerticalFinancialsSheetRow>();

  const columns = indicators.map((indicator) => {
    const documentation = getIndicatorDocumentation(intl, indicator);

    return columnHelper.accessor((row) => (indicator.series[row.index] ? indicator.series[row.index] : null), {
      id: indicator.objectId,
      meta: {
        indicator,
      } as any,
      // See: https://tanstack.com/table/v8/docs/api/core/column-def#meta
      header: () => {
        const { backgroundColor } = indicator.columnTheme;
        const color =
          theme === "dark"
            ? tinycolor(indicator.columnTheme.backgroundColor).darken(25).toRgbString()
            : indicator.columnTheme.textColor;

        return (
          <IndicatorDocumentationPopover indicator={indicator}>
            <PopoverTrigger asChild={true}>
              <div
                className={classNames(
                  "overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600",
                  "hover:cursor-pointer hover:underline",
                )}
                style={{
                  backgroundColor: theme === "dark" ? colors.slate[800] : backgroundColor,
                  color,
                }}
              >
                {documentation.abbreviation || documentation.name}
              </div>
            </PopoverTrigger>
          </IndicatorDocumentationPopover>
        );
      },
      cell: (info) => {
        const resolvedValue = info.getValue();
        const isDarkMode = theme === "dark";
        const cellHasTheme = !!resolvedValue?.theme;
        const isWhiteBackgroundColor = resolvedValue?.theme?.backgroundColor === "#ffffff";
        const isBlackTextColor = resolvedValue?.theme?.textColor === "#000000";
        const isDarkTextColor = tinycolor(resolvedValue?.theme?.textColor).isDark();
        const isSkeleton = resolvedValue?.__typename === "FinancialIndicatorSeriesSkeletonNode";
        let displayValue = resolvedValue ? resolvedValue.formattedValue : "-";
        if (displayValue === "L") {
          // NOTE(Andrey): Remover `description` de todas as mensagens de "P"
          displayValue = intl.formatMessage({
            defaultMessage: "P",
            description: "Abreviação de Prejuízo (Loss) no gráfico de cotação",
            id: "/ywfi1",
          });
        }

        return (
          <div
            className={classNames(
              "whitespace-nowrap",
              "px-4 py-1.5 text-sm font-normal",
              "text-slate-600 dark:text-slate-400",
              "group-data-[selected=false]:text-slate-600",
              "dark:group-data-[selected=false]:text-slate-400",
              "group-data-[selected=true]:!bg-transparent group-data-[selected=true]:!text-inherit",
              "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
              {
                "font-semibold": resolvedValue?.theme?.formatting.includes("BOLD"),
                italic: resolvedValue?.theme?.formatting.includes("ITALIC"),
                "pointer-events-none": !indicator.viewerCanSee,
              },
            )}
            style={{
              backgroundColor: (() => {
                if (isWhiteBackgroundColor) {
                  return undefined;
                }

                if (isDarkMode) {
                  return tinycolor(resolvedValue?.theme?.backgroundColor).darken(15).setAlpha(0.05).toRgbString();
                }

                return resolvedValue?.theme?.backgroundColor;
              })(),
              color: (() => {
                // Dark mode modifiers
                if (isDarkMode) {
                  if (!cellHasTheme) {
                    return undefined;
                  }

                  if (!isDarkTextColor) {
                    return tinycolor(resolvedValue?.theme?.textColor).brighten(5).toRgbString();
                  }
                  if (isBlackTextColor) {
                    return tinycolor(resolvedValue?.theme?.textColor).brighten(70).toRgbString();
                  }
                  return tinycolor(resolvedValue?.theme?.textColor).brighten(30).toRgbString();
                }

                return resolvedValue?.theme?.textColor;
              })(),
            }}
          >
            <span
              className={classNames({
                "animate-pulse rounded-full bg-slate-200 text-transparent dark:bg-slate-700": loading,
                "rounded-full bg-slate-200 text-transparent dark:bg-slate-700": isSkeleton,
              })}
            >
              {displayValue}
            </span>
          </div>
        );
      },
    });
  });

  return [
    // Report Date
    columnHelper.accessor("reportDate", {
      meta: {
        header: {
          className: classNames("sticky left-0 w-24"),
        },
        cell: {
          className: classNames("sticky left-0"),
        },
      },
      header: () => (
        <div
          className={classNames(
            "w-24 bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-700",
            "dark:bg-slate-800 dark:text-slate-300",
          )}
        >
          <FormattedMessage defaultMessage="Ano" id="jbWMDl" description="Ano fiscal dos balanços" />
        </div>
      ),
      cell: (info) => {
        const isTTM = rows[info.row.index].isTrailingTwelveMonths;
        const isPTTM = rows[info.row.index].isPreliminaryTrailingTwelveMonths;
        const isOdd = getYear(rows[info.row.index].reportDate) % 2 === 1;
        const { context } = rows[info.row.index];
        // eslint-disable-next-line no-nested-ternary
        const reportDate = lightFormat(info.getValue(), isPTTM ? `MM/yy 'PTTM'` : isTTM ? `MM/yy 'TTM'` : "MM/yyyy");

        return (
          <div
            className={classNames(
              "select-none",
              "whitespace-nowrap",
              "bg-slate-50 text-slate-600",
              "px-2 py-1.5 text-sm font-semibold text-slate-600",
              "group-hover:bg-violet-50 group-hover:text-violet-700",
              {
                "bg-orange-400/10 text-orange-700": isTTM,
                "bg-emerald-400/20 text-emerald-700": isPTTM,
                "bg-sky-400/10 text-sky-700": isOdd && statementType === IFinancialsStatementType.Quarterly,
                "cursor-pointer": !!context,
              },
              "dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-slate-900 dark:group-hover:text-white",
            )}
          >
            {context && (
              <ReportDateContextPopover side="left" context={context}>
                <PopoverTrigger asChild={true}>
                  <span>{reportDate}</span>
                </PopoverTrigger>
              </ReportDateContextPopover>
            )}

            {!context && isPTTM && (
              <Popover>
                <PopoverTrigger asChild={true}>
                  <span>{reportDate}</span>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  sideOffset={12}
                  align="start"
                  className="max-w-80 rounded-xl border border-transparent bg-black px-2 text-sm text-white"
                >
                  <strong className="font-medium tracking-wide">
                    <FormattedMessage
                      defaultMessage="PTTM são dados preliminares que não foram divulgados na SEC, então podem ter alguns faltando e serão preenchidos quando o 10-Q/K oficial for liberado."
                      id="YSZlyg"
                    />
                  </strong>
                </PopoverContent>
              </Popover>
            )}

            {!context && !isPTTM && <span>{reportDate}</span>}
          </div>
        );
      },
    }),
    ...columns,
  ];
}
